import {FONT_SIZE_MAPPING} from '../../constants';

/* ---------------------------------------------------------- */
/*                  UNWRAP FONT TAGS (PUBLIC)                 */
/* ---------------------------------------------------------- */

export function unwrapFontTags(collection: HTMLCollectionOf<Element>): void {
    const fonts = Array.from(collection);

    // Process inner → outer
    for (let i = fonts.length - 1; i >= 0; i--) {
        const font = fonts[i];
        if (!font.parentElement) continue;
        unwrapFont(font);
    }
}

/* ---------------------------------------------------------- */
/*                       MAIN PROCESSOR                       */
/* ---------------------------------------------------------- */

function unwrapFont(fontEl: Element) {
    const parent = fontEl.parentElement!;
    const fontStyle = getFontStyles(fontEl);
    const children = Array.from(fontEl.childNodes);

    const hasBlock = Array.from(fontEl.children).some(isBlock);

    if (hasBlock) {
        // Handle block children
        for (const child of children) {
            if (child instanceof HTMLElement && child.nodeType === 1) {
                const el = child as HTMLElement;

                if (el.tagName === 'P') {
                    wrapParagraphContent(el, fontStyle); // Special: lexical restriction, do not use styles directly on paragraphs
                } else {
                    mergeStyles(el, fontStyle); // Normal: apply styles directly to other block elements
                }
            }
            parent.insertBefore(child, fontEl);
        }
        parent.removeChild(fontEl);
        return;
    }

    // INLINE CASE — produce spans
    const output: Node[] = [];

    for (const node of children) {
        if (node.nodeType === Node.TEXT_NODE) {
            const span = document.createElement('span');
            span.textContent = node.textContent ?? '';
            span.setAttribute('style', styleToString(fontStyle));
            output.push(span);
            continue;
        }

        if (node.nodeType === 1) {
            const el = node as HTMLElement;

            mergeStyles(el, fontStyle);
            const flattened = splitNestedSpans(el);
            output.push(...flattened);
            continue;
        }

        output.push(node);
    }

    for (const item of output) parent.insertBefore(item, fontEl);
    parent.removeChild(fontEl);
}

/* ---------------------------------------------------------- */
/*                      WRAP <p> CONTENT                      */
/* ---------------------------------------------------------- */

function wrapParagraphContent(p: HTMLElement, style: StyleMap) {
    // Check if <p> already contains a single styled span
    const children = Array.from(p.childNodes);

    // Wrap entire paragraph content in a span
    const span = document.createElement('span');
    span.setAttribute('style', styleToString(style));

    for (const c of children) span.appendChild(c);

    const flat = splitNestedSpans(span);

    // Clear paragraph
    p.innerHTML = '';

    for (const s of flat) p.appendChild(s);
}

/* ---------------------------------------------------------- */
/*              NO NESTED SPANS (span → span forbidden)       */
/* ---------------------------------------------------------- */

function splitNestedSpans(el: HTMLElement): HTMLElement[] {
    if (el.tagName !== 'SPAN') return [el];

    const result: HTMLElement[] = [];
    const parentStyle = parseStyle(el.getAttribute('style') ?? '');

    for (const child of Array.from(el.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE) {
            const span = document.createElement('span');
            span.textContent = child.textContent ?? '';
            span.setAttribute('style', styleToString(parentStyle));
            result.push(span);
            continue;
        }

        if (child.nodeType === Node.ELEMENT_NODE) {
            const elementChild = child as HTMLElement;

            if (elementChild.tagName === 'SPAN') {
                const combined = mergeStyleMaps(
                    parentStyle,
                    parseStyle(elementChild.getAttribute('style') ?? '')
                );

                const innerSplit = splitNestedSpans(elementChild);

                for (const s of innerSplit) {
                    mergeStyles(s, combined);
                    result.push(s);
                }
                continue;
            }

            // Other elements
            mergeStyles(elementChild, parentStyle);
            result.push(elementChild);
            continue;
        }

        // Comment, DocumentFragment, etc.
        result.push(child as HTMLElement);
    }

    return result;
}

/* ---------------------------------------------------------- */
/*                       STYLE HELPERS                        */
/* ---------------------------------------------------------- */

interface StyleMap {
    color?: string;
    'font-size'?: string;
    'font-family'?: string;
}

function mergeStyleMaps(baseStyles: StyleMap, newStyles: StyleMap): StyleMap {
    const result: StyleMap = {...baseStyles};

    if (newStyles.color) result.color = newStyles.color;
    if (newStyles['font-family']) result['font-family'] = newStyles['font-family'];
    if (newStyles['font-size']) result['font-size'] = newStyles['font-size'];

    return result;
}

function mergeStyles(el: HTMLElement, incoming: StyleMap) {
    const current = parseStyle(el.getAttribute('style') ?? '');
    const merged = mergeStyleMaps(current, incoming);
    el.setAttribute('style', styleToString(merged));
}

function getFontStyles(el: Element): StyleMap {
    const out: StyleMap = {};
    const color = el.getAttribute('color');
    const size = el.getAttribute('size');
    const face = el.getAttribute('face') || el.getAttribute('family');

    if (color) out.color = color;
    if (size) out['font-size'] = normalizeFontSize(size);
    if (face) out['font-family'] = face;

    return out;
}

function normalizeFontSize(size: string): string {
    if (FONT_SIZE_MAPPING[size]) return FONT_SIZE_MAPPING[size];
    const n = Number(size);
    return isNaN(n) ? size : `${n}px`;
}

function parseStyle(str: string): StyleMap {
    const out: StyleMap = {};
    str.split(';')
        .map(s => s.trim())
        .filter(Boolean)
        .forEach(part => {
            const [key, val] = part.split(':').map(s => s.trim());
            if (!key || !val) return;
            if (key === 'color') out.color = val;
            if (key === 'font-size') out['font-size'] = val;
            if (key === 'font-family') out['font-family'] = val;
        });
    return out;
}

function styleToString(map: StyleMap): string {
    return Object.entries(map)
        .map(([k, v]) => `${k}: ${v};`)
        .join(' ');
}

function isBlock(el: Element): boolean {
    return [
        'P',
        'DIV',
        'H1',
        'H2',
        'H3',
        'H4',
        'H5',
        'H6',
        'UL',
        'OL',
        'LI',
        'BLOCKQUOTE'
    ].includes(el.tagName);
}
